import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";

import {
  Popover,
  Tooltip,
  IconButton,
  Typography,
  Paper,
  Grid,
  InputLabel,
  TextField,
  makeStyles,
  MenuItem,
  Select
} from "@material-ui/core";
import ReplayIcon from "@material-ui/icons/Replay";
import { FilterList } from "@material-ui/icons";

import { fetchSitesService } from "../../../services/dcm/sites";

const useFilterStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    padding: "24px 24px 36px 24px",
    minWidth: "300px",
    maxWidth: "300px",
    overflow: "none"
  },
  header: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  title: {
    display: "inline-block",
    marginLeft: "7px",
    color: theme.palette.text.primary,
    fontSize: "14px",
    fontWeight: 500
  },
  resetLink: {
    marginLeft: "16px",
    fontSize: "12px",
    cursor: "pointer"
  },
  select: {
    marginTop: "16px"
  }
}));

export const Filter = ({ setFilters, filters }) => {
  const classes = useFilterStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const placementData = useSelector((state) => state.dcmPlacements.data);
  const placementInfo = useSelector((state) => state.placementInfo);
  const campaigns = useSelector((state) => state.dcmCampaigns);
  const [placementSites, setPlacementSites] = useState([]);
  let placementSizeValues = [];
  let leadTimes = [];

  // Helper functions
  function sentenceCase(input) {
    let word = input.slice(0, 1) + input.slice(1).toLowerCase();
    return word.replace("_", "").replace("_", " ");
  }

  function formatDate(input, isNumerical = true) {
    if (isNumerical) input = parseInt(input);

    const date = new Date(input);
    return [
      ("0" + (date.getUTCMonth() + 1)).slice(-2),
      ("0" + date.getUTCDate()).slice(-2),
      ("0" + date.getUTCFullYear()).slice(-2)
    ].join("/");
  }

  // Status filter options
  const statuses = ["Not submitted", "Default creative assigned", "Submitted"];

  // Site filter options
  const getSitesByIds = async (placementData) => {
    if (placementData.length === 0) return [];
    let uniqueSiteIds = [];
    placementData.map((placement) => {
      if (!uniqueSiteIds.includes(placement.siteId)) {
        uniqueSiteIds.push(placement.siteId);
      }
      return placement;
    });
    return await fetchSitesService(uniqueSiteIds);
  };

  useEffect(() => {
    getSitesByIds(placementData)
      .then((sites) => {
        setPlacementSites(sites);
      })
      .catch((err) => console.log(err));
  }, [placementData]);

  // Start filter options
  let start = Object.values(placementInfo.byId).map(
    (placement) => placement.Start_Date
  );
  start = [...new Set(start)];

  // End filter options
  let end = Object.values(placementInfo.byId).map(
    (placement) => placement.End_Date
  );
  end = [...new Set(end)];

  // Lead time filter options
  Object.values(placementInfo.byId).map((placement) => {
    const startDate = new Date(placement.Start_Date);
    const now = new Date();
    const diff = startDate - now;
    const oneDay = 1000 * 60 * 60 * 24;
    const day = Math.floor(diff / oneDay);

    if (!leadTimes.includes(day)) {
      leadTimes.push(day);
    }

    return null;
  });

  // Last modified filter options
  let lastModified = placementData.map(
    (placement) => placement.lastModifiedInfo.time
  );
  lastModified = [...new Set(lastModified)];

  // Dimensions filter options
  placementData.map((placement) => {
    const size = `${placement.size.width} x ${placement.size.height}`;

    if (!placementSizeValues.includes(size)) {
      placementSizeValues.push(size);
    }

    return null;
  });

  // Compatibility filter options
  let compatibility = placementData.map((placement) => placement.compatibility);
  compatibility = [...new Set(compatibility)];

  // Serving types filter options
  let servingTypes = Object.values(placementInfo.byId).map((value) => {
    return value.Serving_Type;
  });
  servingTypes = [...new Set(servingTypes)];

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFilter = (filter, value) => {
    if (Array.isArray(value) ) {
      value = value.join(',');
    }
    setFilters({
      ...filters,
      [filter]: value
    });
  };

  const handleFilterReset = useCallback(() => {
    setFilters({
      status: "All",
      site: "All",
      start: "All",
      end: "All",
      leadTime: "All",
      lastModified: "All",
      dimensions: "All",
      compatibility: "All",
      servingType: "All",
    });
  }, [setFilters]);

  useEffect(() => {
    handleFilterReset();
  }, [campaigns.selectedId, handleFilterReset]);

  const renderFilterSelection = () => {
    return (
      <Paper className={classes.root}>
        <div className={classes.header}>
          <Typography className={classes.title} variant="h6">
            Filter Results
          </Typography>
          <Tooltip title="Reset All">
            <IconButton onClick={handleFilterReset}>
              <ReplayIcon />
            </IconButton>
          </Tooltip>
        </div>
        <Grid container>
          <Grid className={classes.select} item xs={12}>
            <TextField
              select
              onChange={(e) => handleFilter("status", e.target.value)}
              value={filters.status}
              fullWidth
              variant="outlined"
              margin="dense"
              label="Status"
            >
              <MenuItem value="All">All</MenuItem>
              {statuses.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid className={classes.select} item xs={12}>
            <TextField
              select
              onChange={(e) => handleFilter("site", e.target.value)}
              value={filters.site}
              fullWidth
              variant="outlined"
              margin="dense"
              label="Site"
            >
              <MenuItem value="All">All</MenuItem>
              {placementSites.map((value) => (
                <MenuItem key={value.id} value={value.id}>
                  {value.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid className={classes.select} item xs={12}>
            <InputLabel>Start</InputLabel>
            <Select 
              multiple={true} 
              onChange={(e) => handleFilter("start", e.target.value)}
              value={filters.start.split(',')} 
              fullWidth
              variant="outlined"
              margin="dense"
              label="Start"
            >
              <MenuItem value="All">All</MenuItem>
              {start.map((value) => (
                <MenuItem key={value} value={value}>
                  {formatDate(value, false)}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid className={classes.select} item xs={12}>
            <InputLabel>End</InputLabel>
            <Select
              select
              onChange={(e) => handleFilter("end", e.target.value)}
              value={filters.end.split(',')}
              fullWidth
              variant="outlined"
              margin="dense"
              label="End"
            >
              <MenuItem value="All">All</MenuItem>
              {end.map((value) => (
                <MenuItem key={value} value={value}>
                  {formatDate(value, false)}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid className={classes.select} item xs={12}>
            <TextField
              select
              onChange={(e) => handleFilter("leadTime", e.target.value)}
              value={filters.leadTime}
              fullWidth
              variant="outlined"
              margin="dense"
              label="Lead Time"
            >
              <MenuItem value="All">All</MenuItem>
              {leadTimes.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid className={classes.select} item xs={12}>
            <TextField
              select
              onChange={(e) => handleFilter("lastModified", e.target.value)}
              value={filters.lastModified}
              fullWidth
              variant="outlined"
              margin="dense"
              label="Last Modified"
            >
              <MenuItem value="All">All</MenuItem>
              {lastModified.map((value) => (
                <MenuItem key={value} value={value}>
                  {formatDate(value)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid className={classes.select} item xs={12}>
            <TextField
              select
              onChange={(e) => handleFilter("dimensions", e.target.value)}
              value={filters.dimensions}
              fullWidth
              variant="outlined"
              margin="dense"
              label="Dimensions"
            >
              <MenuItem value="All">All</MenuItem>
              {placementSizeValues.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid className={classes.select} item xs={12}>
            <TextField
              select
              onChange={(e) => handleFilter("compatibility", e.target.value)}
              value={filters.compatibility}
              fullWidth
              variant="outlined"
              margin="dense"
              label="Compatibility"
            >
              <MenuItem value="All">All</MenuItem>
              {compatibility.map((value) => (
                <MenuItem key={value} value={value}>
                  {sentenceCase(value)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid className={classes.select} item xs={12}>
            <TextField
              select
              onChange={(e) => handleFilter("servingType", e.target.value)}
              value={filters.servingType}
              fullWidth
              variant="outlined"
              margin="dense"
              label="Serving Type"
            >
              <MenuItem value="All">All</MenuItem>
              {servingTypes.map((value) => (
                <MenuItem key={value} value={value ? value : "N/A"}>
                  {value ? value : "N/A"}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <>
      <Tooltip title="Filter" disableFocusListener>
        <IconButton onClick={handleOpen}>
          <FilterList />
        </IconButton>
      </Tooltip>
      <Popover
        disableScrollLock={true}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
      >
        {renderFilterSelection()}
      </Popover>
    </>
  );
};
